// §9.6 prior-call composed-prompt format. The Preview composer overlay
// shows the full prompt as plain text in a monospace pane — what the
// agent will see at dispatch.
//
// The shape here is fixed (per design handoff README "Composed-prompt
// preview format"): the live composer must replicate it exactly so the
// preview matches what the agent actually sees. Until the backend ships
// a /preview endpoint, this is computed client-side from the contact +
// the operator's L4 override.

import type { Contact, LifecycleStage } from '../../../types/contact';
import { LIFECYCLE_STAGE_LABELS } from '../../../types/contact';

export interface ComposedPromptInputs {
  contact: Contact;
  /** Operator-overridable L4 prior-call summary. */
  priorCallSummary: string;
  /** Count of prior calls included in L4 (capped at 10 by §9.6). */
  priorCallCount: number;
  /** Active campaign name (snapshot, e.g., from active enrollment). */
  campaignName?: string | null;
  /** L2 campaign AI context — value prop, tone, target persona. */
  campaignContext?: string | null;
  /** Stage at dispatch time. Distinct from contact.lifecycle_stage in case the
   * cockpit composes a preview after a pending transition. */
  stage: LifecycleStage;
  /** Free-text position description ("step 1 attempt 1"), if known. */
  campaignPosition?: string | null;
  /** Scheduled-callback hint ("tomorrow 14:00 AEST (high confidence)"), if any. */
  callbackHint?: string | null;
  /** Agent baseline label, e.g., "Pre-sales SDR · v2". */
  agentBaseline?: string | null;
}

const DEFAULT_AGENT_BASELINE =
  'You are a pre-sales SDR. Be consultative, never pushy.';
const DEFAULT_CAMPAIGN_CONTEXT =
  '(no campaign context — running with workspace defaults).';

function fullName(c: Contact): string {
  return [c.first_name, c.last_name].filter(Boolean).join(' ').trim() || '(no name)';
}

function contactProfileLine(c: Contact): string {
  const head = [
    fullName(c),
    c.title || null,
  ]
    .filter(Boolean)
    .join(' · ');
  const contactBits = [c.phone, c.email].filter(Boolean).join(' · ');
  return contactBits ? `${head}\n${contactBits}` : head;
}

export function composePrompt(inputs: ComposedPromptInputs): string {
  const {
    contact,
    priorCallSummary,
    priorCallCount,
    campaignName,
    campaignContext,
    stage,
    campaignPosition,
    callbackHint,
    agentBaseline,
  } = inputs;

  const layer1 = agentBaseline?.trim() || DEFAULT_AGENT_BASELINE;
  const layer2 = campaignContext?.trim() || DEFAULT_CAMPAIGN_CONTEXT;
  const layer4Body = priorCallSummary.trim() || '(no prior calls — first dispatch)';

  const stateLines = [
    `lifecycle: ${stage}`,
    campaignName ? `campaign: ${campaignName}` : null,
    campaignPosition ? `position: ${campaignPosition}` : null,
    callbackHint ? `freestanding_callback: ${callbackHint}` : null,
  ].filter(Boolean);

  return [
    '# Composed prompt (read-only preview)',
    '',
    '[L1 · Agent baseline]',
    layer1,
    '',
    '[L2 · Campaign context]',
    layer2,
    '',
    '[L3 · Contact profile]',
    contactProfileLine(contact),
    '',
    `[L4 · Last 10 calls (${priorCallCount})]`,
    layer4Body,
    '',
    '[L5 · Call-time state]',
    stateLines.join('\n'),
  ].join('\n');
}

export const PRIOR_CALL_LAYERS = [
  { id: 'L1', label: 'Agent baseline', mutability: 'static · per agent' },
  { id: 'L2', label: 'Campaign context', mutability: 'editable on Campaign' },
  { id: 'L3', label: 'Contact profile', mutability: 'editable here' },
  { id: 'L4', label: 'Last 10 calls', mutability: 'append-only' },
  { id: 'L5', label: 'Call-time state', mutability: 'derived at dispatch' },
] as const;

export type PriorCallLayer = (typeof PRIOR_CALL_LAYERS)[number];

export interface LayerSummary {
  detail: string;
  /** How many calls this contact has on file (used by L4 row label). */
  count?: number;
}

export function buildLayerSummaries(inputs: {
  contact: Contact;
  stage: LifecycleStage;
  agentBaseline?: string | null;
  campaignName?: string | null;
  campaignContext?: string | null;
  priorCallCount: number;
  priorCallLastDuration?: string | null;
  priorCallLastDisposition?: string | null;
  campaignPosition?: string | null;
}): Record<PriorCallLayer['id'], string> {
  const stageLabel = LIFECYCLE_STAGE_LABELS[inputs.stage] ?? inputs.stage;
  const l4Bits = [
    `${inputs.priorCallCount} prior`,
    inputs.priorCallLastDuration,
    inputs.priorCallLastDisposition,
  ]
    .filter(Boolean)
    .join(' · ');
  return {
    L1: inputs.agentBaseline || 'Pre-sales SDR · v1',
    L2:
      inputs.campaignContext ||
      (inputs.campaignName
        ? `Active campaign: ${inputs.campaignName}`
        : '(no active campaign)'),
    L3: contactProfileLine(inputs.contact),
    L4: l4Bits || '(no prior calls — first dispatch)',
    L5: `lifecycle: ${stageLabel}${
      inputs.campaignPosition ? ` · ${inputs.campaignPosition}` : ''
    }`,
  };
}
